import React, { useId, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import './IconButton.module.scss'

const VARIANT_CLASS = {
  neutral: 'Uic-IconButton-neutral',
  primary: 'Uic-IconButton-primary',
  danger: 'Uic-IconButton-danger',
}
const TOOLTIP_DELAY = { show: 250, hide: 400 }
const TOOLTIP_TRIGGER = ['hover', 'focus']

const propTypes = {
  /** Font Awesome icon class, e.g. "fas fa-wrench" */
  icon: PropTypes.string.isRequired,
  /** Accessible label — used as the aria-label, and as the tooltip when `tooltip` is omitted */
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  /** Visual emphasis. Reserve `primary` for a single recommended action per group; `danger` for destructive. */
  variant: PropTypes.oneOf(['neutral', 'primary', 'danger']),
  /** Tooltip content; falls back to `label` when omitted. */
  tooltip: PropTypes.node,
  disabled: PropTypes.bool,
}

/**
 * Small square icon-only action button (22x22). The icon-only member of the button family;
 * pair several inside a row's action group. Always requires a `label` for accessibility.
 */
function IconButton({ icon, label, onClick, variant = 'neutral', tooltip = null, disabled = false }) {
  const tooltipId = useId()
  const [show, setShow] = useState(false)
  const overlayContent = tooltip ?? label

  const handleClick = (event) => {
    setShow(false)
    event.currentTarget.blur()
    onClick(event)
  }

  const button = (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      className={clsx('Uic-IconButton-base', VARIANT_CLASS[variant] ?? VARIANT_CLASS.neutral)}
    >
      <i className={icon} aria-hidden="true" />
    </button>
  )

  if (!overlayContent) {
    return button
  }

  return (
    <OverlayTrigger
      placement="top"
      trigger={TOOLTIP_TRIGGER}
      delay={TOOLTIP_DELAY}
      show={show}
      onToggle={setShow}
      overlay={<Tooltip id={tooltipId}>{overlayContent}</Tooltip>}
    >
      {disabled ? <span className="d-inline-flex">{button}</span> : button}
    </OverlayTrigger>
  )
}

IconButton.propTypes = propTypes

export default React.memo(IconButton)
